import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';

import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {useUserLookup} from 'src/hooks/use-user-lookup';
import {usePageTitle} from 'src/hooks/usePageTitle';
import {generateLoginLink, sendLoginLink, uninviteUserById} from 'src/openapi/admin/apis/UserIdentityApi';
import {updateUserRole} from 'src/openapi/admin/apis/UserRoleApi';
import {GetLoginLinkResponse} from 'src/openapi/admin/models/GetLoginLinkResponse';
import {Role} from 'src/openapi/admin/models/Role';
import {User} from 'src/openapi/admin/models/User';
import {isVendorAdminRole} from 'src/utils/role-utils';

import {Status, usePushNotification} from '@pb/kit-component-push-notification';
import Tabs, {Tab} from '@pb/kit-component-tabs-v2';

import {DeleteUserDialog} from '../../components/delete-user-dialog';
import {useAvailableRoles} from '../access-management/hooks/use-available-roles';
import {useRoles} from '../access-management/hooks/use-roles';
import {ProfileTabSeparator} from '../main/components/separator/profile-tab-separator';

import {AuditLogTab} from './components/audit-log-tab';
import {PermissionDetailsCard} from './components/permission-details-card';
import {UserDetailsCard} from './components/user-details-card';
import {UserProfileHeader} from './components/user-profile-header';
import {UserProfileSkeleton} from './components/user-profile-skeleton';

import {
    container,
    section,
    sections,
} from './user-profile.module.styl';


type Params = {
    userId?: string,
};

export const UserProfile: React.FC = () => {
    usePageTitle('Client Portal: Access Management');
    const navigate = useNavigate();
    const {userId: userIdParam} = useParams<Params>();
    const userId = Number(userIdParam);
    const {onCreateNote} = usePushNotification();
    const {executeWithNotifications} = useApiWithNotifications();
    const {lookupUserById, loading: userLoading, clearError} = useUserLookup();
    const {roles, loading: rolesLoading} = useRoles(userId);
    const {availableRoles} = useAvailableRoles();

    const [user, setUser] = useState<User | null>(null);
    const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(undefined);
    const [initialRoleId, setInitialRoleId] = useState<string | undefined>(undefined);
    const [busy, setBusy] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [sendLinkBusy, setSendLinkBusy] = useState(false);
    const [generateLinkBusy, setGenerateLinkBusy] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const selectedTab = searchParams.get('tab') || 'profile';
    const isLoading = userLoading || rolesLoading;
    const canEditRoles = availableRoles.length > 0;
    const prevUserIdRef = useRef(userId);

    useEffect(() => {
        if (prevUserIdRef.current !== userId) {
            prevUserIdRef.current = userId;
            setSearchParams({}, {replace: true});
        }
        lookupUserById(userId)
            .then(fetched => {
                if (fetched) {
                    setUser(fetched);
                    setSelectedRoleId(fetched.role?.id);
                    setInitialRoleId(fetched.role?.id);
                }
            })
            .catch(err => {
                const message = err instanceof Error ? err.message : String(err);
                console.error('Failed to load user:', message);
            });
    }, [clearError, lookupUserById, userId, setSearchParams]);

    const currentRoleId = user?.role?.id;
    const isVendorAdmin = isVendorAdminRole(currentRoleId);

    const roleItems: Array<{value: string, label: string}> = useMemo(() => {
        const items = roles.map((role: Role) => ({
            value: role.id,
            label: role.name,
        }));
        const currentRole = user?.role;
        if (currentRole && !items.some(item => item.value === currentRole.id)) {
            items.push({value: currentRole.id, label: currentRole.name});
        }
        return items.sort((a, b) => a.label.localeCompare(b.label));
    }, [roles, user?.role]);

    const displayName = useMemo(() => {
        if (user) {
            const name = `${user.firstName} ${user.lastName}`;
            if (name.length > 0) {
                return name;
            }
            if (user.email) {
                return user.email;
            }
        }
        return '';
    }, [user]);

    const vendorName = user?.vendor?.vendorName ?? '—';

    const isDirty = user ? selectedRoleId !== initialRoleId : false;
    const saveDisabled =
        !user ||
        !selectedRoleId ||
        !isDirty ||
        busy ||
        deleteBusy ||
        rolesLoading ||
        userLoading ||
        isVendorAdmin;

    const handleRoleChange = useCallback((roleId: string) => {
        setSelectedRoleId(roleId);
    }, []);

    const handleSave = useCallback(async () => {
        if (!user || !selectedRoleId) {
            return;
        }
        setBusy(true);
        try {
            const apiCall = updateUserRole({
                userId: user.userId,
                roleId: selectedRoleId,
            });
            const result = await executeWithNotifications(
                apiCall,
                {
                    success: 'Changes successfully saved',
                    error: 'Failed to save changes',
                    exception: 'Unable to save changes',
                },
                'update-user-role',
            );
            if (result.success) {
                setInitialRoleId(selectedRoleId);
            } else if (result.data) {
                console.error('Failed to save changes:', result.data);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.error('Failed to save changes:', message);
        } finally {
            setBusy(false);
        }
    }, [executeWithNotifications, selectedRoleId, user]);

    const handleDelete = useCallback(async () => {
        if (!user) {
            return;
        }
        setDeleteBusy(true);
        try {
            const result = await executeWithNotifications(
                uninviteUserById({userId: user.userId}),
                {
                    success: 'User successfully deleted',
                    error: 'Failed to delete user',
                    exception: 'Unable to delete user',
                },
                'delete-user-role',
            );

            if (result.success) {
                navigate('/access-management');
            } else if (result.data) {
                console.error('Failed to delete user:', result.data);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.error('Failed to delete user:', message);
        } finally {
            setDeleteBusy(false);
            setShowDeleteDialog(false);
        }
    }, [executeWithNotifications, navigate, user]);

    const handleSendLoginLink = useCallback(async () => {
        if (!user) {
            return;
        }
        setSendLinkBusy(true);
        try {
            await executeWithNotifications(
                sendLoginLink({userId: user.userId}),
                {
                    success: 'Login link sent to user\'s email',
                    error: 'Failed to send login link',
                    exception: 'Unable to send login link',
                },
                'send-login-link',
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.error('Failed to send login link:', message);
        } finally {
            setSendLinkBusy(false);
        }
    }, [executeWithNotifications, user]);

    const handleGenerateLoginLink = useCallback(async () => {
        if (!user) {
            return;
        }
        setGenerateLinkBusy(true);
        try {
            const result = await executeWithNotifications<GetLoginLinkResponse>(
                generateLoginLink({userId: user.userId}),
                {
                    success: 'Login link copied to clipboard',
                    error: 'Failed to generate login link',
                    exception: 'Unable to generate login link',
                },
                'generate-login-link',
            );
            if (result.success && result.data?.loginLink && navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(result.data.loginLink).catch(() => {});
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            console.error('Failed to generate login link:', message);
        } finally {
            setGenerateLinkBusy(false);
        }
    }, [executeWithNotifications, user]);

    const handleClose = useCallback(() => {
        setShowDeleteDialog(false);
    }, []);

    const handleCopyId = useCallback(() => {
        if (user?.userId && navigator?.clipboard?.writeText) {
            const copiedText = String(user.userId);
            navigator.clipboard.writeText(copiedText)
                .then(() => {
                    onCreateNote({
                        message: 'User ID copied to your clipboard',
                        id: `copy-user-id-${Date.now()}`,
                        status: Status.SUCCESS,
                        autoHideTimeout: 2000,
                    });
                })
                .catch(() => {});
        }
    }, [onCreateNote, user?.userId]);


    return (
        <div className={container}>
            <UserProfileHeader
                displayName={displayName}
                userId={user?.userId}
                avatarUrl={user?.avatarUrl || ''}
                onCopyId={handleCopyId}
                onDelete={() => setShowDeleteDialog(true)}
                onSave={handleSave}
                onSendLoginLink={handleSendLoginLink}
                onGenerateLoginLink={handleGenerateLoginLink}
                saveDisabled={saveDisabled}
                deleteDisabled={!user || deleteBusy || userLoading}
                saveBusy={busy}
                deleteBusy={deleteBusy}
                sendLoginLinkBusy={sendLinkBusy}
                sendLoginLinkDisabled={!user || sendLinkBusy || userLoading}
                generateLoginLinkBusy={generateLinkBusy}
                generateLoginLinkDisabled={!user || generateLinkBusy || userLoading}
            />
            <Tabs tabsId="user-profile-tabs" tabPanelId="user-profile-panel" value={selectedTab}>
                <Tab value="profile" caption="User Profile" onClick={() => setSearchParams({}, {replace: true})}/>
                <Tab value="audit-log" caption="Audit Log" onClick={() => setSearchParams({tab: 'audit-log'}, {replace: true})}/>
            </Tabs>
            <ProfileTabSeparator/>
            <div>
                {selectedTab === 'profile' && isLoading ? (
                    <UserProfileSkeleton/>
                ) : null}
                {selectedTab === 'profile' && !isLoading ? (
                    <div className={sections}>
                        <div className={section}>
                            <UserDetailsCard user={user} vendorName={vendorName}/>
                        </div>
                        <div className={section}>
                            <PermissionDetailsCard
                                roleItems={roleItems}
                                selectedRoleId={selectedRoleId}
                                onRoleChange={handleRoleChange}
                                disabled={!user || rolesLoading || userLoading || !canEditRoles || isVendorAdmin}
                            />
                        </div>
                    </div>
                ) : null}
                {selectedTab === 'audit-log' && userId ? (
                    <AuditLogTab userId={userId}/>
                ) : null}
            </div>
            <DeleteUserDialog
                shown={showDeleteDialog}
                onClose={handleClose}
                onConfirm={handleDelete}
                busy={deleteBusy}
            />
        </div>
    );
};
